import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { cn } from '../utils/cn';

type GoalSelectProps = {
  value: string;
  onChange: (value: string) => void;
  isFirstTime: boolean;
  className?: string; 
};

type PreviousGoal = {
  id: string;
  text: string;
  use_count: number;
};

export function GoalSelect({ value, onChange, isFirstTime, className }: GoalSelectProps) {
  const [previousGoals, setPreviousGoals] = useState<PreviousGoal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInput, setShowInput] = useState(isFirstTime);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPreviousGoals();
  }, []);

  const fetchPreviousGoals = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: goals, error } = await supabase
        .from('previous_goals')
        .select('id, text, use_count')
        .eq('user_id', user.id)
        .order('last_used_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPreviousGoals(goals || []);
    } catch (error) {
      console.error('Error fetching previous goals:', error);
      setError('Failed to load previous goals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoalSelect = async (selectedGoal: string) => {
    if (selectedGoal === 'other') {
      setShowInput(true);
      onChange('');
      return;
    }

    onChange(selectedGoal);

    // Update use count and last_used_at for the selected goal
    try {
      const goal = previousGoals.find(g => g.text === selectedGoal);
      if (goal) {
        const { error } = await supabase
          .from('previous_goals')
          .update({
            // Don't increment here as it will be incremented again in handleInputSubmit
            last_used_at: new Date().toISOString()
          })
          .eq('id', goal.id);

        if (error) throw error;
      }
    } catch (error: any) {
      console.error('Error updating goal usage:', error);
    }
  };

  const handleInputSubmit = async () => {
    if (!value.trim()) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if this goal already exists
      const existingGoal = previousGoals.find(g => g.text.toLowerCase() === value.toLowerCase());
      
      if (existingGoal) {
        // Update existing goal
        const { error } = await supabase
          .from('previous_goals')
          .update({
            use_count: existingGoal.use_count + 1,
            last_used_at: new Date().toISOString()
          })
          .eq('id', existingGoal.id);

        if (error) throw error;
      } else {
        // Insert new goal
        const { data, error } = await supabase
          .from('previous_goals')
          .insert({
            text: value.trim(),
            user_id: user.id,
            status: 'active'
          })
          .select();

        if (error) throw error;
        
        // Add the new goal to the local state
        if (data && data.length > 0) {
          setPreviousGoals(prev => [data[0], ...prev]);
        }
      }

      // Refresh the goals list
      await fetchPreviousGoals();
      setShowInput(false);
    } catch (error: any) {
      console.error('Error saving goal:', error);
      setError('Failed to save goal');
    }
  };

  // Handle Enter key press in the input field
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  if (isLoading) {
    return <div className="text-gray-500">Loading...</div>;
  }

  if (showInput) {
    return (
      <div className={cn("space-y-4", className)}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your goal or pressing problem..."
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent appearance-none"
          autoFocus
        />
        <div className="flex justify-between">
          <button
            onClick={handleInputSubmit}
            disabled={!value.trim()}
            className={cn(
              "text-[#236caf] font-medium hover:text-[#004060]",
              !value.trim() && "opacity-50 cursor-not-allowed"
            )}
          >
            Save Goal
          </button>
          {!isFirstTime && (
            <button
              onClick={() => setShowInput(false)}
              className="text-[#236caf] hover:text-[#004060] text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => handleGoalSelect(e.target.value)}
          className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent appearance-none bg-white text-base"
          style={{
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none'
          }}
        >
          <option value="">Select a previous goal...</option>
          {previousGoals.map(goal => (
            <option key={goal.id} value={goal.text}>
              {goal.text} ({goal.use_count}x)
            </option>
          ))}
          <option value="other">+ Add new goal</option>
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>

      {error && (
        <p className="text-red-600 text-sm">{error}</p>
      )}
    </div>
  );
}